import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  TextField,
  Typography
} from '@mui/material';
import { isEmpty } from 'lodash';
import { FC, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { API_SLUGS } from '../../../../constants';
import { axiosInstance } from '../../../../services/axios';
import { IAlertState, useAlertState } from '../../../../store/alertState';
import { Plus } from '../../../../icons/plus';
import { Close as CloseIcon, DeleteOutline } from '@mui/icons-material';

interface IAddJiraFormValues {
  name: string;
  host: string;
  email: string;
  apiToken: string;
  isActive?: boolean;
}

interface IFormStatus {
  successMessage: string;
  errorMessage: string;
}

interface IAddForm extends RouteComponentProps {
  updateOnly?: boolean;
  defaultFormValues?: any;
  updateUUID?: string;
  setJiraStatus: any;
}

const JiraIntegrationForm: FC<IAddForm> = ({ defaultFormValues, setJiraStatus }) => {
  const [formValuesList, setFormValuesList] = useState<IAddJiraFormValues[]>([
    { name: "", host: "", email: "", apiToken: "", isActive: false, ...defaultFormValues }
  ]);
  const [formStatusList, setFormStatusList] = useState<IFormStatus[]>([
    { successMessage: "", errorMessage: "" }
  ]);
  const { setMessage } = useAlertState(state => state) as IAlertState;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updateOnly, setUpdateOnly] = useState(false);

  useEffect(() => {
    fetchJiraIntegration();
  }, []);

  const fetchJiraIntegration = async () => {
    try {
      const response = await axiosInstance.get(API_SLUGS.CLOUD_INTEGRATION);
      const jira = response?.data?.jira;
      if (!isEmpty(jira)) {
        setFormValuesList(jira?.servers || []);
        setFormStatusList((jira?.servers || []).map(() => ({ successMessage: '', errorMessage: '' })));
        setUpdateOnly(true)
      }
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message;
      setMessage({ title: errorMsg, type: "error" });
    }
  };

  const handleChange = (value: string, type: keyof IAddJiraFormValues, index: number) => {
    const updatedValues = [...formValuesList];
    updatedValues[index] = { ...updatedValues[index], [type]: value };
    setFormValuesList(updatedValues);
  };

  const isNameDuplicate = (name: string, currentIndex: number) => {
    return formValuesList.some((form, index) => index !== currentIndex && form?.name === name);
  };

  const handleGlobalSave = async () => {
    const updatedStatus = [...formStatusList];
    let hasError = false;
  
    formValuesList?.forEach((form, i) => {
      if (!form?.name?.trim() || !form?.host?.trim()) {
        updatedStatus[i] = { successMessage: "", errorMessage: "Please fill all the mandatory fields" };
        hasError = true;
      } else if (isNameDuplicate(form?.name, i)) {
        updatedStatus[i] = { successMessage: "", errorMessage: "Server name must be unique" };
        hasError = true;
      } else {
        updatedStatus[i] = { successMessage: "", errorMessage: "" };
      }
    });
  
    setFormStatusList(updatedStatus);
    if (hasError) return;
  
    setIsSubmitting(true);
    try {
      await saveAllForms(formValuesList);
      setMessage({ title: "Jira details saved successfully", type: "success" });
      fetchJiraIntegration();
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message;
      setMessage({ title: errorMsg, type: "error" });
    } 
      setIsSubmitting(false);
    
  };
  
  const saveAllForms = async (forms: IAddJiraFormValues[]) => {
    if(updateOnly) {
      return await axiosInstance.patch(API_SLUGS.CLOUD_INTEGRATION, {
        type: "jira",
        details: { servers: forms }
      });
    } else {
      return await axiosInstance.post(API_SLUGS.CLOUD_INTEGRATION, {
        type: "jira",
        details: { servers: forms }
      });
    }
   
  };


  const handleAddNewForm = () => {
    setFormValuesList([
      ...formValuesList,
      { name: "", host: "", email: "", apiToken: "", isActive: false }
    ]);
    setFormStatusList([
      ...formStatusList,
      { successMessage: "", errorMessage: "" }
    ]);
  };

  const handleRemoveForm = (index: number) => {
    const updatedForms = [...formValuesList];
    const updatedStatus = [...formStatusList];
    updatedForms.splice(index, 1);
    updatedStatus.splice(index, 1);
    setFormValuesList(updatedForms);
    setFormStatusList(updatedStatus);
  };

  return (
    <Grid container spacing={3}>
      {formValuesList?.map((formValues, index) => (
        <Grid key={index} item xs={12}>
          <Card elevation={5}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant='h6'>
                    {formValues?.name ? `${formValues?.name} - ` : 'Jira Server - '}
                    <Typography variant='h6' component="span" color={formValues?.isActive ? 'secondary' : 'error'}>
                      {formValues?.isActive ? 'Active' : 'Inactive'}
                    </Typography>
                  </Typography>
                </Box>
                {formValuesList?.length > 1 && (
                  <IconButton onClick={() => handleRemoveForm(index)} color="error">
                    <DeleteOutline />
                  </IconButton>
                )}
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    sx={{ maxWidth: "50%" }}
                    label="Server Name"
                    value={formValues.name}
                    onChange={(e) => handleChange(e.target.value, 'name', index)}
                    required
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    sx={{ maxWidth: "50%" }}
                    label="Host URL"
                    value={formValues.host}
                    onChange={(e) => handleChange(e.target.value, 'host', index)}
                    required
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    sx={{ maxWidth: "50%" }}
                    label="Username or Email"
                    value={formValues.email}
                    onChange={(e) => handleChange(e.target.value, 'email', index)}
                    required
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    sx={{ maxWidth: "50%" }}
                    label="API token"
                    value={formValues.apiToken}
                    onChange={(e) => handleChange(e.target.value, 'apiToken', index)}
                    required
                    variant="outlined"
                  />
                </Grid>

                {(formStatusList?.[index]?.successMessage || formStatusList?.[index]?.errorMessage) && (
                  <Grid item xs={12}>
                    {formStatusList?.[index]?.successMessage && (
                      <Alert severity="success" sx={{ width: "fit-content" }}>
                        {formStatusList?.[index]?.successMessage}
                      </Alert>
                    )}
                    {formStatusList?.[index]?.errorMessage && (
                      <Alert severity="error" sx={{ width: "fit-content" }}>
                        {formStatusList?.[index]?.errorMessage}
                      </Alert>
                    )}
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ))}

      <Grid item xs={12}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={handleAddNewForm}>
            Add New Server <Plus />
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={handleGlobalSave}
            disabled={isSubmitting}
          >
            Save All
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
};

export default withRouter(JiraIntegrationForm);